<template>
  <div class="ops-phone" data-test="ops-phone">
    <div class="ops-phone__tabs" role="tablist" aria-label="Practice Ops sections" data-test="ops-phone-tabs">
      <button
        v-for="item in tabs"
        :key="item.id"
        type="button"
        role="tab"
        class="ops-tab"
        :aria-selected="tab === item.id ? 'true' : 'false'"
        :class="{ 'ops-tab--on': tab === item.id, 'ops-tab--icon': item.id === 'alerts' }"
        :data-test="`ops-phone-tab-${item.id}`"
        @click="tab = item.id"
      >
        <svg v-if="item.id === 'alerts'" viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" /><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" /></svg>
        <span :class="{ 'ops-tab__sr': item.id === 'alerts' }">{{ item.label }}</span><span v-if="item.count" class="ops-tab__count" :data-test="`ops-phone-count-${item.id}`">{{ item.count }}</span>
      </button>
    </div>

    <div class="ops-phone__sub" data-test="ops-phone-sub">{{ subA }}<span class="ops-phone__warn">{{ subB }}</span></div>

    <div class="ops-phone__body">
      <div v-if="skeleton" class="ops-phone__skel" aria-hidden="true"><span></span><span></span><span></span></div>

      <template v-else-if="tab === 'checkin'">
        <OpsPatientCard v-for="card in board.checkin" :key="card.key" v-bind="cardProps(card)" v-on="cardListeners" />
        <div v-if="!board.checkin.length" class="ops-phone__empty" data-test="ops-phone-empty">No one waiting</div>
      </template>

      <template v-else-if="tab === 'rooms'">
        <section v-for="room in board.rooms" :key="room.opNum" class="ops-phone__room" :data-test="`ops-phone-room-${room.opNum}`">
          <div class="ops-phone__roomhead">
            <span class="ops-phone__roomname">{{ room.label }}</span>
            <span class="ops-phone__roomsub">{{ roomSubtitle(room) }}</span>
          </div>
          <OpsPatientCard v-for="card in room.cards" :key="card.key" v-bind="cardProps(card)" v-on="cardListeners" />
          <div v-if="!room.cards.length" class="ops-phone__open">Room open</div>
        </section>
        <div v-if="!board.rooms.length" class="ops-phone__empty" data-test="ops-phone-empty">No rooms are set up yet.</div>
      </template>

      <template v-else-if="tab === 'checkout'">
        <OpsPatientCard v-for="card in board.checkout" :key="card.key" v-bind="cardProps(card)" v-on="cardListeners" />
        <div v-if="!board.checkout.length" class="ops-phone__empty" data-test="ops-phone-empty">No one at check-out</div>
      </template>

      <template v-else>
        <!-- Web push for pings (plan §5): hidden unless this build has a VAPID key and the browser can do push. -->
        <OpsEnableAlertsCard v-if="pushEmployeeId" :employee-id="pushEmployeeId" :site-key="pushSiteKey" />
        <button type="button" class="ops-phone__send" data-test="ops-phone-send" @click="$emit('send-alert')">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.85" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" /><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" /></svg>
          Send alert
        </button>
        <OpsPingCard v-for="ping in pings.pings" :key="ping.key" :ping="ping" @clear="$emit('clear-ping', $event)" />
        <div v-if="!pings.pings.length" class="ops-phone__empty" data-test="ops-phone-empty">No alerts. A ping stays here until someone clears it.</div>
      </template>
    </div>

    <OpsMoveSheet
      v-if="sheetCard"
      :card="sheetCard"
      :rooms="board.rooms"
      :can-dismiss="sheetCard.column === 'checkout' && !sheetCard.dismissed"
      @move="onSheetMove"
      @checkout="onSheetCheckout"
      @dismiss="onSheetDismiss"
      @ping="onSheetPing"
      @close="sheetRef = null"
    />
  </div>
</template>

<script>
/**
 * The Practice Ops Phone view (plan §6, design frame "Phone — Check-in"): four underline tabs — Check-in, Operatories, Check-out,
 * Alerts (a bell with its count: the design has three tabs, the pings are one tap away) — of ONE column of `touch` cards. NO drag (a thumb scrolls; it must never move a patient), NO chat (the Board has it).
 *
 * A card's ⋯ opens the Move-to bottom sheet (OpsMoveSheet): Check-in, every room (an occupied room is greyed and cannot be
 * picked, with its occupant's name — behaviour call 1), Check-out, Dismiss, Send alert. Choosing emits ONE event:
 *
 *   request-move({ card, to })   `to` is 'checkin' | 'checkout' | an OperatoryNum. The PAGE decides (moves.js planMove: the
 *                                occupied-room refusal, the toast) and commits through the classic moveCard — the same handler a
 *                                Board drop and the tablet's Seat next use.
 *   ping-card(card) · send-alert · alert-click({ card, key, el }) · dismiss-card(card) · clear-ping(ping)
 *
 * The Alerts tab also carries the "Enable alerts on this phone" card (OpsEnableAlertsCard: web push for pings). It draws itself
 * only when this build has a VAPID key and the browser can do push; `pushEmployeeId` / `pushSiteKey` say who and where.
 *
 * The cards, their icons (Schedule View's) and the Alerts tab's ping cards are the Board's own components.
 *
 * ⛔ No prop is named `patNum`, `siteID`, `loading` or `allSettings` (mixin.js computeds shadow them).
 */
import OpsPatientCard from './OpsPatientCard.vue';
import OpsPingCard from './OpsPingCard.vue';
import OpsMoveSheet from './OpsMoveSheet.vue';
import OpsEnableAlertsCard from './OpsEnableAlertsCard.vue';
import { waitingLine, checkoutLine, roomSub } from '@/utils/practiceOps/boardText';

export default {
  name: 'OpsPhone',

  components: { OpsPatientCard, OpsPingCard, OpsMoveSheet, OpsEnableAlertsCard },

  props: {
    /** board.js buildBoard() */
    board: { type: Object, required: true },
    /** { pings, legend, hasLegend } — boardText.js buildPings() */
    pings: { type: Object, default: () => ({ pings: [], legend: [], hasLegend: false }) },
    zone: { type: String, default: '' },
    nowMs: { type: Number, default: 0 },
    alertPayload: { type: Object, default: null },
    viewSettings: { type: Object, default: null },
    showAlertIcons: { type: Boolean, default: true },
    skeleton: { type: Boolean, default: false },
    stripeFor: { type: Function, default: () => '' },
    textFor: { type: Function, default: card => ({ proc: card.procDescript || '', note: card.note || '' }) },
    birthdayFor: { type: Function, default: () => false },
    providerFor: { type: Function, default: () => '' },
    /** The signed-in employee and their office, for the web-push card ('' ⇒ no card). */
    pushEmployeeId: { type: String, default: '' },
    pushSiteKey: { type: String, default: '' },
  },

  data() {
    return {
      tab: 'checkin',
      /** The card whose ⋯ was tapped, by identity ({ patNum, aptNum }) so the sheet follows the live board. */
      sheetRef: null,
    };
  },

  computed: {
    tabs() {
      const c = this.board.counts;
      return [
        { id: 'checkin', label: 'Check-in', count: c.checkin },
        { id: 'rooms', label: 'Operatories', count: c.occupiedRooms },
        { id: 'checkout', label: 'Check-out', count: c.checkedOut },
        { id: 'alerts', label: 'Alerts', count: this.pings.pings.length },
      ];
    },

    /** The line under the tabs, per tab: "3 waiting" + the amber " · 2 over 15 min". */
    subParts() {
      const c = this.board.counts;
      if (this.tab === 'checkin') {
        const w = waitingLine(c, this.board.thresholdMin);
        return { a: w.a || `${c.checkin} in Check-in`, b: w.b };
      }
      if (this.tab === 'rooms') return { a: `${c.occupiedRooms} of ${this.board.rooms.length} rooms in use`, b: '' };
      if (this.tab === 'checkout') return { a: checkoutLine(c), b: '' };
      const n = this.pings.pings.length;
      return { a: n === 1 ? '1 alert' : `${n} alerts`, b: '' };
    },

    subA() {
      return this.subParts.a;
    },

    subB() {
      return this.subParts.b;
    },

    allCards() {
      const all = [].concat(this.board.checkin, this.board.checkout);
      this.board.rooms.forEach((r) => { r.cards.forEach((c) => { all.push(c); }); });
      return all;
    },

    /** The sheet's card, looked up live; null once it has left the board. */
    sheetCard() {
      const ref = this.sheetRef;
      if (!ref) return null;
      return this.allCards.find(c => c.patNum === ref.patNum && c.aptNum === ref.aptNum) || null;
    },

    cardListeners() {
      return {
        ping: card => this.$emit('ping-card', card),
        'alert-click': payload => this.$emit('alert-click', payload),
        menu: card => this.openSheet(card),
      };
    },
  },

  watch: {
    /** The patient the sheet is for left the board (a move, a refresh): the sheet goes with them. */
    sheetCard(card) {
      if (this.sheetRef && !card) this.sheetRef = null;
    },
  },

  methods: {
    roomSubtitle(room) {
      const first = room.cards[0];
      return roomSub(room, first ? this.providerFor(first) : '', this.nowMs);
    },

    /** Everything one OpsPatientCard is given (a v-bind object). `touch` size; the ⋯ is ours (the sheet), never the inline menu. */
    cardProps(card) {
      const text = this.textFor(card) || {};
      return {
        card,
        size: 'touch',
        stripe: this.stripeFor(card) || '',
        proc: text.proc || '',
        note: text.note || '',
        zone: this.zone,
        alertPayload: this.alertPayload,
        viewSettings: this.viewSettings,
        showAlertIcons: this.showAlertIcons,
        birthday: !!this.birthdayFor(card),
        // the card needs ONE target to draw its ⋯ at all; the sheet lists the real ones
        moveTargets: [{ id: 'checkin', label: 'Check-in' }],
        canDismiss: card.column === 'checkout' && !card.dismissed,
        externalMenu: true,
      };
    },

    openSheet(card) {
      this.sheetRef = { patNum: card.patNum, aptNum: card.aptNum };
    },

    onSheetMove({ card, to }) {
      this.sheetRef = null;
      this.$emit('request-move', { card, to });
    },

    onSheetCheckout(card) {
      this.sheetRef = null;
      this.$emit('request-move', { card, to: 'checkout' });
    },

    onSheetDismiss(card) {
      this.sheetRef = null;
      this.$emit('dismiss-card', card);
    },

    onSheetPing(card) {
      this.sheetRef = null;
      this.$emit('ping-card', card);
    },
  },
};
</script>

<style lang="scss" scoped>
.ops-phone {
  max-width: 560px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

// The underline tabs of Forms Settings (pfe-tab), ported: 3px brand underline on the open tab, neutral ink otherwise.
.ops-phone__tabs {
  display: flex;
  gap: 0;
  overflow-x: auto;
  scrollbar-width: none;
  border-bottom: 1px solid #dfe4e3;
}

.ops-tab {
  position: relative;
  flex: 1 1 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 42px;
  padding: 0 3px;
  border: 0;
  border-bottom: 3px solid transparent;
  background: transparent;
  font: inherit;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
  color: #707876;
  cursor: pointer;

  &:hover { color: #0c0f0e; }
  &--on { color: #0f7259; border-bottom-color: #0f7259; }
  &:focus-visible { outline: 2px solid #0f7259; outline-offset: -2px; }
}

// the bell tab: just the icon and its count; the word is for screen readers
.ops-tab--icon { flex: 0 0 auto; padding: 0 6px; }
.ops-tab--icon svg { display: block; flex: none; }

.ops-tab__sr {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

.ops-tab__count {
  margin-left: 3px;
  font-size: 11.5px;
  font-weight: 700;
  color: #707876;
}

.ops-tab--on .ops-tab__count { color: #0f7259; }

.ops-phone__sub {
  padding: 2px 2px 0;
  font-size: 13px;
  color: #707876;
}

.ops-phone__warn {
  color: #b45309;
  font-weight: 700;
}

.ops-phone__body {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-bottom: 20px;
}

.ops-phone__room {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.ops-phone__roomhead {
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 4px 2px 0;
}

.ops-phone__roomname {
  font-size: 14px;
  font-weight: 800;
}

.ops-phone__roomsub {
  font-size: 12.5px;
  color: #707876;
}

.ops-phone__open,
.ops-phone__empty {
  border: 1.5px dashed #d5dcda;
  border-radius: 12px;
  padding: 14px 12px;
  font-size: 13px;
  line-height: 1.45;
  text-align: center;
  color: #707876;
}

.ops-phone__send {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 44px;
  padding: 0 16px;
  border: 1px solid #b9dccd;
  border-radius: 10px;
  background: #e9f6f1;
  color: #0b5c49;
  font-family: inherit;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;

  svg { display: block; }
}

.ops-phone__skel {
  display: flex;
  flex-direction: column;
  gap: 10px;

  span {
    display: block;
    height: 110px;
    border-radius: 14px;
    background: #e3e8e7;
  }
}
</style>
